import React from 'react';
import _ from 'underscore';

import { MenuItemInfo } from '@tableau/widgets';

import { defaultMemoize } from 'reselect/lib';
import { Messages } from '../../../codegen/Localize';
import { ButtonProps } from '../../../common/react/components/ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu, ButtonControlledFlyoutListMenuProps } from '../../../common/react/components/ButtonControlledFlyoutListMenu';
import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { RecommendationTypeFilterEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { Telemetry } from '../../../commonInfra/services/Telemetry';


export type RecommendationType = 'popular' | 'similar_users' | 'all';

export interface RecommendationTypeInfo {
  displayValue: () => string;
  type: RecommendationType;
}

const RecommendationTypeDictionary: { [V in RecommendationType | null]: RecommendationTypeInfo} = {
  'all' : {
    displayValue: Messages.filterByTypeDropdown_showAll,
    type: 'all'
  },
  'popular' : {
    displayValue: Messages.vizRecommendationsPopularExplanation,
    type: 'popular'
  },
  'similar_users': {
    displayValue: Messages.vizRecommendationsSimiliarUsersExplanation,
    type: 'similar_users'
  }
};

export interface RecommendationTypeDropdownProps {
  readonly recommendationType: RecommendationType;
  readonly recommendationTypeSelected: (value: RecommendationType) => void;
  readonly disabled: boolean;
}

export type RecommendationTypeButtonProps = ButtonProps & RecommendationTypeDropdownProps;

export class RecommendationTypeDropdown extends React.PureComponent<RecommendationTypeDropdownProps> {
  private memoizedPanelContent = defaultMemoize(getPanelContent);

  public constructor(props: RecommendationTypeDropdownProps) {
    super(props);
  }

  public render(): JSX.Element {
    return <ExplorerDropdown
              testId={RecommendationTypeDropdownTestIds.dropDown}
              renderButtonContent={this.renderButtonContent}
              renderPanel={this.renderPanel}
              panelTitle={''}
              disabled={this.props.disabled}
              buttonStyleOverrides={{height: 'auto'}} />;
  }

  private handleSelection = (value: string) => {
    const recommendationType = RecommendationTypeDictionary[value as RecommendationType].type;
    this.props.recommendationTypeSelected(recommendationType);
    this.sendTelemetryEvent(recommendationType);
  }

  private renderButtonContent = (_showingOrActive: boolean, _disabled: boolean): JSX.Element => {
    return (
      <div>
        {RecommendationTypeDictionary[this.props.recommendationType].displayValue()}
      </div>
    );
  }

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return this.memoizedPanelContent(this.handleSelection, props, this.props);
  }

  private sendTelemetryEvent(recommendationType: RecommendationType) {
    const event = CreateExplorerEvent<RecommendationTypeFilterEvent>(
      {
        type: TelemetryEvents.RecommendationTypeFilter,
        recommendationType: recommendationType
      }
    );

    // The recommendations page ignores the global variable that dicatates the value of "event.displayMode".
    // So, to maintain consistency, we need to also ignore it.
    // This hack can be removed if any of the following things happen:
    // 1- We change this dropdown to use the context api as the newer events are
    // 2- The Recommendations page stops ignoring the global variable and show view recs in list mode,
    //    in which case, we should also stop ignoring it
    event.displayMode = 'thumbnail';

    Telemetry.sendEvent<RecommendationTypeFilterEvent>(event);
  }
}

const getPanelContent = (handleSelection: (value: string) => void, flyoutProps: ExplorerFlyoutPanelProps, props: RecommendationTypeDropdownProps): JSX.Element => {
  const menuItems = buildMenuItems(props);
  const panelProps: ButtonControlledFlyoutListMenuProps = {
    onComponentDidMount: _.noop,
    menuItems: menuItems,
    onMenuItemSelected: handleSelection,
    anchorElement: flyoutProps.anchorEl,
    closeMe: flyoutProps.onCloseRequest,
    testId: RecommendationTypeDropdownTestIds.childPanelWrapper,
    id: `${flyoutProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
    role: 'menu',
  };
  return <ButtonControlledFlyoutListMenu {...panelProps} />;
};

const buildMenuItems = (props: RecommendationTypeDropdownProps): MenuItemInfo[] => {
  const items: MenuItemInfo[] = [];

  items.push({
    value: RecommendationTypeDictionary.all.type,
    displayValue: RecommendationTypeDictionary.all.displayValue(),
    disabled: false,
    checked:  props.recommendationType === RecommendationTypeDictionary.all.type
  });

  items.push({
    value: RecommendationTypeDictionary.popular.type,
    displayValue: RecommendationTypeDictionary.popular.displayValue(),
    disabled: false,
    checked: props.recommendationType === RecommendationTypeDictionary.popular.type
  });

  items.push({
    value: RecommendationTypeDictionary.similar_users.type,
    displayValue: RecommendationTypeDictionary.similar_users.displayValue(),
    disabled: false,
    checked:  props.recommendationType === RecommendationTypeDictionary.similar_users.type
  });

  return items;
};

export namespace RecommendationTypeDropdownTestIds {
  export const dropDown = 'spaces-recommendation-type-dropdown';
  export const button = 'spaces-recommendation-type-button';
  export const childPanelWrapper = 'spaces-recommendation-type-panel-wrapper';
}
